/**
 * Gamer Process Handlers
 *
 * Handlers for gamer (REGULAR user) specific functionality
 * These functions should NOT be called by facilitators
 */

import { ApiResponse } from '../types'
import { State } from '../../hooks/useAppState'
import { ActivityWithSession, ActivityWithSessions } from '../../types/GeneralTypes'
import { loadCloudFunctionsHandler } from '../handlers'
import type { UpcomingActivitiesPageData } from '../handlers/cloudFunctions/CloudFunctionsApiSessionHandlers'
import { buildMockUpcomingActivities } from '../mocks/facilitatorLiveSessionMock'
import { OrgRole } from '../../../../pure-js/types/OrganizationMembership'
import { isParticipantOrgRole } from '../../../../pure-js/libs/organizationMembershipHelpers'
import { getActiveOrgRole } from '../../features/organization/orgStateHelpers'
import { handleRequest } from './config'

/**
 * Get upcoming activity for gamer (singular - for game starting)
 * Uses backend cloud function getUpcomingActivityForGamer
 * Returns ONE activity with a single session for session management
 *
 * NOTE: This function is ONLY for gamers and admins
 * Facilitators should NOT call this function
 */
export const getUpcomingActivityForGamer = async (
  state: State
): Promise<ApiResponse<ActivityWithSession | undefined>> => {
  const orgRole = getActiveOrgRole(state.user)

  // Guard: only participants and admins have a gamer activity
  if (orgRole === OrgRole.FACILITATOR) {
    console.warn('⚠️ [ProcessHandler] getUpcomingActivityForGamer called by facilitator - this should not happen')
    return {
      data: undefined,
      success: false,
      error: 'This function is only available for gamers, not facilitators'
    }
  }

  // Only participant roles and admins have a gamer activity
  const canHaveGamerActivity = isParticipantOrgRole(orgRole) || orgRole === OrgRole.ADMIN
  if (orgRole && !canHaveGamerActivity) {
    console.warn('⚠️ [ProcessHandler] getUpcomingActivityForGamer called with invalid org role:', orgRole)
    return {
      data: undefined,
      success: false,
      error: 'Invalid user type for this function'
    }
  }

  try {
    const cloudFunctions = await loadCloudFunctionsHandler()
    return await cloudFunctions.getUpcomingActivityForGamer(state)
  } catch (error) {
    console.error('❌ [ProcessHandler] Error calling getUpcomingActivityForGamer:', error)
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch upcoming activity'
    return {
      data: undefined,
      success: false,
      error: errorMessage
    }
  }
}

/**
 * Get upcoming activities list for gamer (plural - for dashboard/upcoming page)
 * Uses backend cloud function getUpcomingActivities (same as facilitator)
 * Returns array of all activities
 */
export const getUpcomingActivitiesListForGamer = async (state: State): Promise<ApiResponse<ActivityWithSessions[]>> => {
  return handleRequest<ApiResponse<ActivityWithSessions[]>>(
    async () => ({
      data: buildMockUpcomingActivities(),
      success: true
    }),
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        const response = await cloudFunctions.getUpcomingActivities(state)

        // Backend already returns ActivityWithSessions format, no transformation needed
        const activities = response.data || []

        return {
          data: activities,
          success: response.success
        }
      } catch (error) {
        console.error('❌ [ProcessHandler] Error calling getUpcomingActivities:', error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch upcoming activities'
        return {
          data: [],
          success: false,
          error: errorMessage
        }
      }
    }
  )
}

/**
 * Offset-paged upcoming activities for infinite scroll (gamer "all activities" list).
 * Returns the page plus total/hasMore/nextOffset so the caller can request the next slice.
 */
export const getUpcomingActivitiesPageForGamer = async (
  state: State,
  { limit = 20, offset = 0 }: { limit?: number; offset?: number } = {}
): Promise<ApiResponse<UpcomingActivitiesPageData>> =>
  handleRequest<ApiResponse<UpcomingActivitiesPageData>>(
    async () => {
      const all = buildMockUpcomingActivities()
      const activities = all.slice(offset, offset + limit)
      const nextOffset = offset + activities.length
      return {
        data: { activities, total: all.length, hasMore: nextOffset < all.length, nextOffset },
        success: true
      }
    },
    async () => {
      try {
        const cloudFunctions = await loadCloudFunctionsHandler()
        return await cloudFunctions.getUpcomingActivitiesPaged(state, { limit, offset })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch upcoming activities'
        return {
          data: { activities: [], total: 0, hasMore: false, nextOffset: offset },
          success: false,
          error: errorMessage
        }
      }
    }
  )
